import { apiCall } from './apiClient'
import { validate } from '@repo/validation/validation'
import { useToastStore } from '@repo/store/useToastStore'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'

const apiUrlBase =
  import.meta.env.VITE_SPA_OSPREY_API_URL ||
  import.meta.env.VITE_OUTLOOK_ADDIN_API_URL ||
  'https://osprey-SERVICENAME-api.ospreydev.co.uk'

const securityApiUrlBase =
  import.meta.env.VITE_SPA_OSPREY_SECURITY_API_URL || import.meta.env.VITE_OUTLOOK_ADDIN_SECURITY_API_URL || undefined

// This module provides functions to use the API from Osprey

const unauthenticatedEndpoints = [
  'User/Authenticate',
  'User/OutlookAuthenticate',
  'User/RefreshAuthentication',
  'User/DeleteRefreshTokenCookie',
  'User/PasswordResetRequest',
  'User/PasswordResetUpdate',
  'User/GetMinimumPasswordLength'
]

export const ospreyAPI = async ({
  service,
  endpoint,
  method = 'GET',
  data,
  schema,
  headers = { 'Content-Type': 'application/json; charset=utf-8' },
  makeApiCall = apiCall,
  successToast = null,
  errorToast = null,
  suppressErrorToast = false,
  suppress404 = false,
  withCredentials = false,
  sendAsUrlParams = false,
  responseType = 'json',
  timeout = 60000,
  shouldTrimObjectStringValues = true
}) => {
  let apiHost
  if (service === 'security' && securityApiUrlBase) apiHost = securityApiUrlBase
  else apiHost = apiUrlBase?.replace('SERVICENAME', service) || ''

  if (!apiHost || !service || !endpoint) {
    return Promise.reject('Missing API parameters')
  }

  if (!useToastStore.getState().isOnline) {
    return Promise.reject(new Error('Offline: API call blocked'))
  }

  const { url, requiresAuth } = getUrlAndAuthRequirements(endpoint, apiHost)
  const { isLoggedIn } = useAuthenticationStore.getState()

  try {
    if (schema && data) await validate(schema, data)

    const res = await makeApiCall({
      method,
      url,
      requiresAuth,
      data,
      withCredentials,
      isLoggedIn,
      sendAsUrlParams,
      headers,
      responseType,
      timeout,
      shouldTrimObjectStringValues
    })

    if (successToast) useToastStore.getState().showToast(successToast)

    return res?.data || res
  } catch (err) {
    console.log(err)
    const status = err?.status || err?.response?.status || err?.cause?.status || ''
    const isNotAuthenticated = status === 401
    const failedRefresh = endpoint === 'User/RefreshAuthentication'

    if ((requiresAuth && isNotAuthenticated) || failedRefresh) {
      redirectToLogout()
    } else if (!window.location.hash.includes('auth')) {
      const toast = getErrorToastConfig({ toast: errorToast, suppressErrorToast, suppress404, status, endpoint })
      if (toast) useToastStore.getState().showToast(toast)
    }

    return Promise.reject(err)
  }
}

const redirectToLogout = () => {
  if (!window.location.hash.includes('auth')) {
    // if the auth token is invalid or missing, we need to log the user out
    // this will clear the auth token and refresh token
    // and redirect the user to the login page
    console.log('Redirecting to logout page')
    window.location.hash = '/auth/logout'
  }
}

const getUrlAndAuthRequirements = (endpoint, apiHost) => {
  const endpointWithoutLeadingSlash = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint
  const url = `${apiHost}/${endpointWithoutLeadingSlash}`
  const requiresAuth = !unauthenticatedEndpoints.includes(endpointWithoutLeadingSlash)

  return { url, requiresAuth }
}

const getErrorToastConfig = ({ toast, suppressErrorToast, suppress404, status, endpoint }) => {
  if (suppressErrorToast) return null
  if (suppress404 && status === 404) return null
  if (status === 498) return null
  if (toast) return toast

  let title = 'Error'
  let text = `${status || status === 0 ? `${status} ` : ''}Error calling the API: `
  let highlight = endpoint

  if (status === 403) {
    title = 'Access denied'
    text = `You do not have permission to perform this action: `
  }

  return { type: 'error', text, title, highlight }
}
